import debounce from 'lodash/debounce';
import { hasUserConsentedToPersonalizedAds } from './userConsentLoader';
import {
    DfpTargeting,
    Slot,
    Slots,
    GoogleTag,
    CustomWindow,
    RecordedAdData,
    SlotRenderEndedEvent
} from '../types';
import { LAZY_LOAD_OPTS } from '../constants';
import { logInfo } from 'shared/logger';

declare const window: CustomWindow;

/**
 * Class should only be initialised on the client side as it depends on window
 */
class DfpManager {
    /** Object to hold all ad render event details for use in reporting */
    adDataMap: { [key: string]: RecordedAdData[] };
    /** Enable debug logging */
    private debug: boolean;
    /** Determine if device is 'mobile' or 'desktop' for targeting values */
    private desktopBreakpoint: number;
    /** Object to hold ids of slots that have a breakpoint */
    dynamicSizeBreakpoints: { [key: number]: string[] };
    /** Unique identifier of the instantiated class */
    private id: number;
    /** Object to hold references to currently active dfp slots by id */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    opt_slots: { [key: string]: any };
    /** Current active key value targeting Object */
    targetingConfigMap: DfpTargeting;
    /** Value of previous window width */
    private windowWidthState: number;

    constructor(targeting: DfpTargeting, debug: boolean) {
        this.adDataMap = {};
        this.debug = debug || false;
        this.desktopBreakpoint = 960;
        this.dynamicSizeBreakpoints = {};
        this.id = Math.floor(Math.random() * Math.floor(100));
        this.opt_slots = {};
        this.targetingConfigMap = targeting || {};
        this.windowWidthState = window.innerWidth;

        this.recordAdRender = this.recordAdRender.bind(this);
        this.log = this.log.bind(this);
    }

    get googletag(): GoogleTag {
        const windowRef = window;
        windowRef.googletag = windowRef.googletag || { cmd: [] };
        return windowRef.googletag;
    }

    log(...args: unknown[]): void {
        if (this.debug) {
            logInfo(`AdProvider/DfpManager(${this.id}):`, ...args);
        }
    }

    /** Replace current targeting object */
    configureTargeting(data: DfpTargeting) {
        this.log('configureTargeting', { data });
        this.targetingConfigMap = { ...data };
    }

    getRuntimeUsersPersonalisedAdsConsent() {
        return hasUserConsentedToPersonalizedAds(document);
    }

    setRuntimeGlobalTargetingConfiguration(googletag = this.googletag) {
        const targetingConfigMap = {
            ...this.targetingConfigMap,
            ...this.dynamicTargeting()
        };
        const keys = Object.keys(targetingConfigMap);
        if (keys.length > 0) {
            googletag.cmd.push(function () {
                keys.forEach((key) => {
                    googletag
                        .pubads()
                        .setTargeting(key, targetingConfigMap[key]);
                });
            });
        }
    }

    defineSlot(slot: Slot, googletag = this.googletag) {
        const {
            sizes,
            id,
            position,
            adUnitPath,
            collapseEmpty,
            hideFromSizeDown,
            hideFromSizeUp
        } = slot;
        const sizesArray = sizes.length > 1 ? sizes : sizes[0];
        const opt_slots = this.opt_slots;

        if (hideFromSizeUp || hideFromSizeDown) {
            const bps = this.dynamicSizeBreakpoints;
            const sz = hideFromSizeUp || (hideFromSizeDown as number);
            bps[sz] = bps[sz] ? [...bps[sz], id] : [id];
        }

        googletag.cmd.push(function () {
            opt_slots[id] = googletag
                .defineSlot(adUnitPath, sizesArray, id)
                ?.setCollapseEmptyDiv(collapseEmpty || false)
                .addService(googletag.pubads())
                .setTargeting('pos', position.toString());

            if (hideFromSizeDown) {
                const mapping = googletag
                    .sizeMapping()
                    .addSize([hideFromSizeDown, 0], sizesArray)
                    .addSize([0, 0], [])
                    .build();
                opt_slots[id]?.defineSizeMapping(mapping);
            }
            if (hideFromSizeUp) {
                const mapping = googletag
                    .sizeMapping()
                    .addSize([hideFromSizeUp, 0], [])
                    .addSize([0, 0], sizesArray)
                    .build();
                opt_slots[id]?.defineSizeMapping(mapping);
            }
        });
    }

    destroySlot(id: string, googletag = this.googletag) {
        const logRef = this.log;
        const slot = this.opt_slots[id];
        logRef('destroySlot', id);
        googletag.cmd.push(function () {
            googletag.destroySlots([slot]);
        });
        delete this.opt_slots[id];
    }

    refreshTargeting(googletag = this.googletag) {
        this.log('refreshTargeting');
        googletag.cmd.push(function () {
            googletag.pubads().clearTargeting();
        });
        this.setRuntimeGlobalTargetingConfiguration();
    }

    refreshIds(ids: string[], googletag = this.googletag) {
        const refreshArr = ids.map((id) => this.opt_slots[id]);
        googletag.cmd.push(function () {
            googletag.pubads().refresh(refreshArr);
        });
    }

    refresh(googletag = this.googletag) {
        /* TODO: refresh only viewable ads: https://developers.google.com/doubleclick-gpt/guides/control-ad-loading */
        const logRef = this.log;
        googletag.cmd.push(function () {
            logRef('pubads.refresh()');
            googletag.pubads().refresh();
        });
    }

    recordAdRender(event: SlotRenderEndedEvent) {
        const map = this.adDataMap;
        const slotId = event.slot.getSlotElementId();
        map[slotId] = map[slotId] || [];
        map[slotId].push({
            creativeId: event.creativeId,
            sourceAgnosticCreativeId: event.sourceAgnosticCreativeId
        });
        this.log(`slotRenderEnded(${slotId})`, { event });
    }

    resizeRefresh(currWidth: number, prevWidth: number) {
        this.log('resizeRefresh', currWidth, prevWidth);
        const bpListeners = Object.keys(
            this.dynamicSizeBreakpoints
        ).map((num) => parseInt(num));
        if (bpListeners.length && currWidth !== prevWidth) {
            const idsToUpdate = bpListeners.reduce((arr, bp) => {
                const dontUpdate =
                    (prevWidth > bp && currWidth > bp) || // no threshold change
                    (prevWidth < bp && currWidth < bp); //no threshold change
                if (dontUpdate) return arr;
                return [...arr, ...this.dynamicSizeBreakpoints[bp]];
            }, [] as string[]);
            if (idsToUpdate.length > 0) this.refreshIds(idsToUpdate);
        }
        this.windowWidthState = currWidth;
    }

    resizeWatcher() {
        const handler = () => {
            this.resizeRefresh(window.innerWidth, this.windowWidthState);
        };
        window.addEventListener('resize', debounce(handler, 1500));
    }

    dynamicTargeting(): DfpTargeting {
        const isMobile = window.innerWidth < this.desktopBreakpoint;
        return {
            platform: isMobile ? 'mobile' : 'desktop',
            device: isMobile ? 'mobile' : 'desktop',
            os:
                (typeof window === 'object' && window?.navigator?.platform) ||
                ''
        };
    }

    defineSlots(slots: Slots) {
        const slotIds = Object.keys(slots);
        slotIds.forEach((id) => this.defineSlot(slots[id]));
    }

    // Should only be called ONCE per genuine page load, otherwise refresh where needed
    init(callback: (error?: Error) => void, googletag = this.googletag) {
        const personalizedAdsEnabled = this.getRuntimeUsersPersonalisedAdsConsent();
        const recordAdRenderRef = this.recordAdRender;

        this.setRuntimeGlobalTargetingConfiguration();
        this.resizeWatcher();

        const onComplete = () => {
            this.log('initComplete', { this: this });
            if (callback) callback();
        };

        this.log('initWithConfig', { personalizedAdsEnabled });

        googletag.cmd.push(function () {
            googletag
                .pubads()
                .addEventListener('slotRenderEnded', recordAdRenderRef);
            googletag.pubads().setCentering(true);
            googletag.pubads().enableLazyLoad(LAZY_LOAD_OPTS);
            googletag.pubads().enableSingleRequest();
            googletag
                .pubads()
                .setRequestNonPersonalizedAds(personalizedAdsEnabled ? 0 : 1);
            googletag.enableServices();
            onComplete();
        });
    }
}

export default DfpManager;
